<template src="./categoryMatching.html"></template>

<script>
import VueResizable from 'vue-resizable'
import goodsTopMenu from '../components/topMenu';
import {config} from "@/config";
import axios from "axios";
import Multiselect from "vue-multiselect";
import persistentState from "@/mixin/persistentState";

export default {
  name: "CategoryMatching",
  mixins: [persistentState], // Include the mixin
  components: {
    Multiselect,
    VueResizable,
    goodsTopMenu,
  },
  data() {
    return {
      fileUploaded: false,
      sources: [],
      selectedSource: null,
      newSource: '',
      systemCategories: [],
      categoryMappings: [],
      categoryMappingsForSelectedSource: [],
      isSourceSaving: false,
      isCategoryMatchingSaving: false,
      taskId: null,
      isMatchingProcessing: false,
    };
  },
  methods: {
    // Save Persistent Data
    savePersistentState() {
      const dataToSave = {
        fileUploaded: this.fileUploaded,
        selectedSource: this.selectedSource,
        systemCategories: this.systemCategories,
        categoryMappings: this.categoryMappings,
        categoryMappingsForSelectedSource: this.categoryMappingsForSelectedSource,
        taskId: this.taskId,
        isMatchingProcessing: this.isMatchingProcessing,
      };
      this.saveToLocalStorage(dataToSave);
    },

    // Load Persistent Data
    restorePersistentState() {
      const savedData = this.loadFromLocalStorage();
      if (savedData) {
        this.fileUploaded = savedData.fileUploaded || false;
        this.selectedSource = savedData.selectedSource || null;
        this.systemCategories = savedData.systemCategories || [];
        this.categoryMappings = savedData.categoryMappings || [];
        this.categoryMappingsForSelectedSource =
            savedData.categoryMappingsForSelectedSource || [];
        this.taskId = savedData.taskId || null;
        this.isMatchingProcessing = savedData.isMatchingProcessing || false;
      }
    },

    addNewSourceOption() {
      this.sources = this.sources.filter(source => source.id !== 0);

      this.sources.unshift({id: 0, name: '+ Add New Source'});
    },

    async fetchSources() {
      const response = await axios.get(`${config.apiUrl}/source`);
      this.sources = response.data.data;

      this.addNewSourceOption();
    },

    async createSource() {
      if (this.isSourceSaving) {
        return;
      }

      if (this.newSource === '') {
        // TODO Change warning?
        alert('Source Name is required. Please enter it');
        return;
      }

      this.isSourceSaving = true;
      try {
        const response = await axios.post(`${config.apiUrl}/source`, { name: this.newSource });
        if (response.status === 'error') {
          alert(response.data.errorInfo);
          return;
        }

        const source = response.data.data;

        this.sources.push(source);
        this.selectedSource = source;
        this.newSource = '';

        this.addNewSourceOption();

      } catch (e) {
        alert(e.response?.data?.errorInfo || e.message || "An unknown error occurred");
      } finally {
        this.isSourceSaving = false;
      }
    },

    async fetchSystemCategories() {
      const response = await axios.get(`${config.apiUrl}/goods-category-list`);
      this.systemCategories = response.data.data;
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.taskId = null;
      this.isMatchingProcessing = false;

      const formData = new FormData();
      formData.append('file', file);

      axios.post(`${config.apiUrl}/category-matching/upload`, formData)
          .then(response => {
            this.fileUploaded = true;

            this.categoryMappings = response.data.categories.map(cat => ({
              fileCategory: cat,
              systemCategory: null
            }));

            // Apply saved mappings if source is already selected
            if (this.selectedSource && this.selectedSource.id) {
              this.updateCategoryMappings();
            }
          });
    },
    async saveMappings() {
      if (this.isCategoryMatchingSaving) {
        return;
      }

      this.isCategoryMatchingSaving = true;
      try {
        await axios.post(`${config.apiUrl}/category-matching`, {
          sourceId: this.selectedSource?.id,
          mappings: this.categoryMappings,
        });
        this.savePersistentState(); // Save after successful backend call
      } catch (e) {
        alert(e.response?.data?.errorInfo || e.message || "An unknown error occurred");
      } finally {
        this.isCategoryMatchingSaving = false;
      }
    },
    async loadCategoryMappingsForSelectedSource(sourceId) {
      try {
        const response = await axios.get(`/category-matching?sourceId=${sourceId}`);
        this.categoryMappingsForSelectedSource = response.data.data;

        if (this.fileUploaded) {
          // Update existing category mappings
          this.updateCategoryMappings();
        } else {
          // Initialize category mappings with saved mappings from scratch
          this.categoryMappings = this.categoryMappingsForSelectedSource.map(mapping => ({
            fileCategory: mapping.sourceCategory,
            systemCategory: this.findSystemCategoryById(mapping.systemCategory),
            statusSource: 'saved',
          }));
        }
      } catch (error) {
        console.error("Error loading category mappings:", error);
      }
    },
    async startCategoryMatching() {
      this.taskId = null;
      this.isMatchingProcessing = false;

      try {
        // Send a request to fetch categories from the upload endpoint
        const response = await axios.post(`${config.apiUrl}/category-matching/start-matching`, {
          external_categories: this.categoryMappings.map(mapping => mapping.fileCategory)
        });

        if (response.data.status === "success") {
          this.taskId = response.data.data?.task_id; // Save taskId
          this.isMatchingProcessing = true;

          //alert(`Matching started. Task ID: ${this.taskId}`);
        } else {
          alert("Failed to start matching");
        }
      } catch (error) {
        alert(error.response?.data?.errorInfo || error.message || "An unknown error occurred");
      }
    },

    async loadMatches() {
      if (!this.taskId) {
        alert("No task ID available to load matches.");
        return;
      }

      try {
        const response = await axios.get(`${config.apiUrl}/category-matching/load-matches-by-task-id`, {
          params: { taskId: this.taskId }
        });

        const status = response.data.status;

        switch (status) {
          case "success": // Task processing is complete
            console.log("Matches:", response.data.data);
            // alert("Matches loaded successfully!");

            // Update category mappings with matching data
            this.categoryMappings = this.categoryMappings.map(mapping => {
              const match = response.data.data.find(
                m => m.external_category === mapping.fileCategory
              );

              if (match) {
                return {
                  ...mapping,
                  systemCategory: this.findSystemCategoryById(match.crm_id), // Select system category
                  statusSource: "automatic" // Mark it as auto-selected
                };
              }

              return mapping;
            });

            break;

          case "processing": // Task is still in progress
            alert("The matching process is still running. Please try again later.");
            break;

          case "error": // An error occurred
            alert(response.data.message || "An error occurred during the matching process.");
            break;

          case "not_found": // Task ID not found
            alert("Task not found. Please verify the Task ID.");
            break;

          default: // Handle any unexpected statuses
            alert("Unexpected status received from the server.");
            console.error("Unexpected status:", status);
        }

      } catch (error) {
        alert(error.response?.data?.errorInfo || error.message || "An unknown error occurred");
      }
    },

    updateCategoryMappings() {
      this.categoryMappings = this.categoryMappings.map(mapping => {
        const savedMapping = this.categoryMappingsForSelectedSource.find(
            saved => saved?.sourceCategory === mapping.fileCategory
        );
        return {
          ...mapping,
          systemCategory: savedMapping ? this.findSystemCategoryById(savedMapping.systemCategory) : null,
          statusSource: savedMapping ? "saved" : null,
        };
      });
    },

    findSystemCategoryById(categoryId) {
      return this.flattenedCategories.find(cat => cat.id === categoryId) || null;
    },

    updateMappingStatus(mapping) {
      // Change the status to "manual" when a user selects a new category
      mapping.statusSource = "manual";
    },
  },
  computed: {
    flattenedCategories() {
      const flattenTree = (categories, level = 0) => {
        let result = [];
        categories.forEach(category => {
          result.push({
            id: category.id,
            name: `${'— '.repeat(level)}${category.name}`, // Indent child categories
            level: category.level
          });
          if (category.children && category.children.length) {
            result = result.concat(flattenTree(category.children, level + 1));
          }
        });
        return result;
      };

      return flattenTree(this.systemCategories);
    }
  },
  // ============================__COMPUTED__============================
  watch: {
    fileUploaded() {
      this.savePersistentState();
    },
    selectedSource(currentSource) {
      if (currentSource && currentSource.id) {
        this.loadCategoryMappingsForSelectedSource(currentSource.id);
      } else {
        this.categoryMappingsForSelectedSource = [];
      }

      this.savePersistentState();
    },
    categoryMappings: {
      deep: true,
      handler() {
        this.savePersistentState();
      },
    },
    taskId() {
      this.savePersistentState();
    },
    categoryMappingsForSelectedSource: {
      deep: true,
      handler() {
        this.savePersistentState();
      },
    },
    systemCategories: {
      deep: true, // Watch for deep changes in systemCategories
      handler() {
        this.savePersistentState();
      },
    },
    isMatchingProcessing() {
      this.savePersistentState();
    },
  },
  mounted() {
    if (this.$store.getters.getAllowSection.indexOf('product') === -1) {
      this.$router.push({name: 'permissionDenied'});
      return;
    }

    this.fetchSources();
    this.fetchSystemCategories();
  },
  created() {
    // Restore state from localStorage on component creation
    this.restorePersistentState();

    // Add event listener to persist state on browser unload
    window.addEventListener("beforeunload", this.savePersistentState);

    window.addEventListener("resize", this.windowResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.windowResize);

    // Remove event listener to prevent memory leaks
    window.removeEventListener("beforeunload", this.savePersistentState);
  },
};
</script>

<style scoped>
.category-mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.category-mapping-table th,
.category-mapping-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: left;
}

.status-icon {
  font-size: 1.2em;
  font-weight: bold;
  cursor: pointer;
}

.status-icon.orange {
  color: orange;
}

.status-icon.green {
  color: green;
}

.status-icon.blue {
  color: blue;
}

.legend {
  font-size: 1rem;
  display: flex;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Flexbox container for a consistent layout */
.category-cell {
  display: flex;
  align-items: center; /* Vertically center content */
  gap: 10px; /* Distance between the asterisk and the selector */
}

/* Adjust the multiselect behavior */
.category-cell .multiselect {
  flex-grow: 1; /* Allow the selector to take up the available row space */
}
</style>

<style>
.goods-import thead th {
  height: 4.5rem;
}

.goods-import .error-table thead th {
  height: 2rem !important;
}

.goods-import .b-table-sticky-header > .table.b-table > thead > tr > th {
  top: -1px !important;
}

.goods-import .goods-import tr th:first-child {
  width: 5%;
}

.goods-import .table th, .goods-import .table td {
  padding: 0;
  vertical-align: middle;
}

.goods-import html {
  overflow-x: hidden;
}
</style>